import { Component } from "@theme/component";
import { QuantitySelectorUpdateEvent } from "@theme/events";
import { onDocumentReady } from "@theme/utilities";

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  /**
   * Disables the quantity selector.
   */
  disable() {
    const quantityInput = this.refs.quantityInput;
    if (quantityInput) {
      quantityInput.disabled = true;
    }
    this.classList.add("disabled");
  }

  /**
   * Enables the quantity selector.
   */
  enable() {
    const quantityInput = this.refs.quantityInput;
    if (quantityInput) {
      quantityInput.disabled = false;
    }
    this.classList.remove("disabled");
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    )
      return;

    quantityInput.select();
    /* Store fallback value */
    this.storedValue = event.target.value;
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      const newValue = event.target.value;
      const parsedNewValue = parseInt(newValue);

      if (isNaN(parsedNewValue) && this.storedValue) {
        event.target.value = this.storedValue;
        return;
      }
      this.refs.quantityInput.value = newValue;
      this.storedValue = newValue;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#configureButtons();
    const newValue = parseInt(quantityInput.value);
    const sellingPlanId = quantityInput.dataset.sellingPlanId || null;

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine),
        sellingPlanId
      )
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;
    const parsedMin = min ? parseInt(min) : null;
    const parsedMax = max ? parseInt(max) : null;
    const parsedNewValue = parseInt(newValue);

    if (parsedMin && parsedNewValue < parsedMin) quantityInput.value = min;
    if (parsedMax && parsedNewValue > parsedMax) quantityInput.value = max; 
  };

  /**
   * Sets the state of the decrease/increase button.
   * @param {HTMLButtonElement} button - The button.
   * @param {Boolean} disabled - The enabled/disabled boolean value
   */
  #setButtonState = (button, disabled) => {
    button.disabled = disabled;
    button.classList.toggle('disabled', disabled);
  }

  /**
   * Configures decrease/increase buttons based on input's min/max.
   */
  #configureButtons = () => {
    const { quantityInput, quantityButtons } = this.refs;

    if (!Array.isArray(quantityButtons)) return;

    const decreaseButton = quantityButtons.find((button) => button.getAttribute('name') === 'minus');
    const increaseButton = quantityButtons.find((button) => button.getAttribute('name') === 'plus');
    
    if (!(decreaseButton instanceof HTMLButtonElement) || !(increaseButton instanceof HTMLButtonElement)) return;
    
    const { min, max, value } = quantityInput;
    const parsedMin = min ? parseInt(min) : null;
    const parsedMax = max ? parseInt(max) : null;
    const parsedValue = parseInt(value);

    this.#setButtonState(decreaseButton, parsedMin === parsedValue);
    this.#setButtonState(increaseButton, parsedMax === parsedValue);
  }

  /**
   * Initializes quantity selector.
   */
  init() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    // store fallback value
    this.storedValue = quantityInput.value;

    quantityInput.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        quantityInput.blur();
        quantityInput.dispatchEvent(new Event('blur'));
      }
    });

    this.#configureButtons();
  }
 
  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }
}

if (!customElements.get("quantity-selector-component")) {
  customElements.define(
    "quantity-selector-component",
    QuantitySelectorComponent
  );
}
